import useCurrentUser from '~/hooks/useCurrentUser'
import { useAppSelector } from '~/redux/hooks'
import { useGetUserLocationInfoFromIpQuery } from '~/redux/apis/geo-location-api'

import {
  findUsCurriculumStateByName,
  findUsCurriculumStateByStateCode,
  US_GROUPED_COUNTRY_CODES,
  US_OTHER_STATE,
  US_OTHER_STATES_CODE,
  UsCurriculumState,
} from '../constants/usCurriculumStates.const'
import { selectCurriculumName } from '../slice/persistCurriculum.slice'

const US_COUNTRY_CODE = 'US'

interface ResolvedUsCurriculumState {
  /** Undefined whenever `isUnset`. */
  state: UsCurriculumState | undefined
  /** Nothing resolved and no earlier pick: a US account predating the column. Callers gate on the curriculum being US. */
  isUnset: boolean
}

/** Saved column, else (logged-out only) this device's last pick → geo region. A raw unserved code resolves to the catch-all. */
export function useResolvedUsCurriculumState(): ResolvedUsCurriculumState {
  const user = useCurrentUser()
  const persistentCurriculumName = useAppSelector(selectCurriculumName)
  const { data: geoLocation } = useGetUserLocationInfoFromIpQuery(undefined, { skip: Boolean(user) })

  // Leaving the US curriculum cannot clear the column (the API layer strips nulls), so an old code must not outlive it.
  const hasUsCurriculum = US_GROUPED_COUNTRY_CODES.includes(user?.curriculumCountryCode ?? '')
  const savedState =
    hasUsCurriculum && user?.curriculumStateCode
      ? findUsCurriculumStateByStateCode(user.curriculumStateCode) ?? US_OTHER_STATE
      : undefined
  const geoState =
    !user && geoLocation?.countryCode === US_COUNTRY_CODE && geoLocation.region
      ? findUsCurriculumStateByStateCode(geoLocation.region) ?? US_OTHER_STATE
      : undefined
  // Logged out, a pick outranks the IP guess; logged in, the account is the only truth.
  const localState = user ? undefined : findUsCurriculumStateByName(persistentCurriculumName)
  const isUnset = !savedState && !geoState && (!localState || localState.stateCode === US_OTHER_STATES_CODE)
  const state = isUnset ? undefined : savedState ?? localState ?? geoState

  return { state, isUnset }
}
